/**
 * AuthContext
 *
 * Centralized authentication state provider.
 * Replaces the standalone useBackendAuth hook to ensure a single
 * polling timer and consistent auth state across the entire app.
 *
 * Usage:
 *   // Wrap your app root:
 *   <AuthProvider><App /></AuthProvider>
 *
 *   // In any component:
 *   const { user, accessToken, loading, signOut } = useAuth();
 */

import type { User } from "@supabase/supabase-js";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { inferAuthType } from "../../../utils/authType";
import { usePostHog } from "../hooks/usePostHog";
import { captureGoogleOAuthStartBeforeRedirect } from "../utils/posthogConfig";

interface AuthContextType {
    user: User | null;
    accessToken: string | null;
    loading: boolean;
    isSessionChecked: boolean;
    error: string;
    authenticated: boolean;
    /** True when the current session is a fresh signup awaiting a consent decision. */
    needsMarketingConsent: boolean;
    signInWithEmail: (email: string, password: string) => Promise<any>;
    signUpWithEmail: (email: string, password: string) => Promise<any>;
    signInWithGoogle: (redirectUri: string) => void;
    recordMarketingConsent: (marketingConsent: boolean, consentText: string) => Promise<void>;
    changePassword: (
        currentPassword: string,
        password: string,
        confirmPassword: string,
    ) => Promise<{
        password_changed?: boolean;
        password_change_unknown?: boolean;
        error_description?: string;
    }>;
    signOut: (destination?: "signin" | "password-recovery") => Promise<void>;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const { track, identify } = usePostHog();
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSessionChecked, setIsSessionChecked] = useState(false);
    const [error, setError] = useState("");
    const [needsMarketingConsent, setNeedsMarketingConsent] = useState(false);

    const getAuthType = useCallback(() => inferAuthType(new URLSearchParams(window.location.search)), []);

    const loadSession = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await fetch(`/auth/session`, {
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("Failed to load session");
            }

            const data = await response.json();

            if (data.authenticated) {
                // Only update user if the contents actually changed. response.json()
                // returns a fresh object on every poll, and React compares by identity,
                // so assigning it unconditionally makes every hook that depends on
                // `user` re-run every polling interval even when nothing changed.
                // Same reasoning as the accessToken guard below.
                setUser((prev) => (prev && JSON.stringify(prev) === JSON.stringify(data.user) ? prev : data.user));
                setNeedsMarketingConsent(data.needs_marketing_consent === true);
                // Only update accessToken if the value actually changed.
                // This prevents unnecessary re-renders and Realtime subscription teardowns
                // in SupabaseContext which depends on accessToken.
                setAccessToken((prev) => (prev === data.access_token ? prev : data.access_token));

                // Verified login links any pending signed anonymous visitor identity.
                // The server ignores account IDs supplied by the browser.
                if (import.meta.env.VITE_REMOTE_PRODUCT_TELEMETRY_ENABLED === "true") {
                    void fetch("/auth/backend/analytics/link", {
                        method: "POST",
                        credentials: "include",
                        keepalive: true,
                    }).catch(() => {
                        /* Best-effort analytics; never block login. */
                    });
                }

                // Identify usage with auth_type
                identify(data.user.id, {
                    email: data.user.email,
                    auth_type: getAuthType(),
                });
            } else {
                setUser(null);
                setAccessToken(null);
                setNeedsMarketingConsent(false);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Unknown error";
            console.error("[AUTH] Failed to load session", err);
            setError(errorMessage);
            setUser(null);
            setAccessToken(null);
            setNeedsMarketingConsent(false);
        } finally {
            setLoading(false);
            setIsSessionChecked(true);
        }
    }, [
        identify,
        getAuthType,
    ]);

    // Single polling timer for the entire app
    useEffect(() => {
        loadSession();

        // Proactive refresh: Check every 4 minutes
        // Server will rotate token if < 10 mins remaining
        const interval = setInterval(loadSession, 4 * 60 * 1000);

        return () => clearInterval(interval);
    }, [
        loadSession,
    ]);

    const signInWithEmail = useCallback(
        async (email: string, password: string) => {
            setLoading(true);
            setError("");

            try {
                const response = await fetch(`/auth/backend/signin`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        email,
                        password,
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error_description || "Sign in failed");
                }

                setUser(data.user);
                // Reload session to get access_token
                await loadSession();

                // Track successful sign-in and identify user
                identify(data.user.id, {
                    email: data.user.email,
                    auth_type: getAuthType(),
                });
                track("auth_signin_completed", {
                    method: "email",
                    user_id: data.user.id,
                    email: data.user.email,
                    auth_type: getAuthType(),
                });
            } catch (err: any) {
                const errorMessage = err instanceof Error ? err.message : "Unknown error";
                console.error("[AUTH] Sign in failed", err);
                setError(errorMessage);

                track("auth_signin_failed", {
                    method: "email",
                    error_message: errorMessage,
                });

                throw err;
            } finally {
                setLoading(false);
            }
        },
        [
            loadSession,
            track,
            identify,
            getAuthType,
        ],
    );

    const signUpWithEmail = useCallback(
        async (email: string, password: string) => {
            setLoading(true);
            setError("");

            try {
                // Stash the current search params (session_id, is_desktop, etc.) so that
                // after email confirmation we can restore the in-progress flow even though
                // Supabase's email link only returns to the Site URL (no query string).
                // Stored with a timestamp; AuthPage discards entries older than 24h.
                if (window.location.search) {
                    try {
                        localStorage.setItem(
                            "pendingConfirmRedirect",
                            JSON.stringify({
                                search: window.location.search,
                                ts: Date.now(),
                            }),
                        );
                    } catch {
                        /* storage disabled — confirmation will still work, just without desktop context */
                    }
                }

                const response = await fetch(`/auth/backend/signup`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        email,
                        password,
                        auth_type: getAuthType(),
                    }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error_description || "Sign up failed");
                }

                if (data.requires_confirmation) {
                    setError("");

                    // Identify before user has a session so subsequent events on the
                    // CheckEmailView and confirmation step are attributed to this user.
                    if (data.user_id) {
                        identify(data.user_id, {
                            email,
                            auth_type: getAuthType(),
                        });
                    }

                    track("auth_signup_completed", {
                        method: "email",
                        user_id: data.user_id,
                        email,
                        requires_confirmation: true,
                        auth_type: getAuthType(),
                        $set_once: {
                            auth_signup_completed_at: new Date().toISOString(),
                        },
                    });

                    return {
                        success: true,
                        requires_confirmation: true,
                        message: data.message,
                        user_id: data.user_id,
                    };
                }

                setUser(data.user);
                // Reload session to get access_token
                await loadSession();

                // Track successful sign-up and identify user
                identify(data.user.id, {
                    email: data.user.email,
                    auth_type: getAuthType(),
                });
                track("auth_signup_completed", {
                    method: "email",
                    user_id: data.user.id,
                    email: data.user.email,
                    requires_confirmation: false,
                    auth_type: getAuthType(),
                    $set_once: {
                        auth_signup_completed_at: new Date().toISOString(),
                    },
                });

                return {
                    success: true,
                    requires_confirmation: false,
                };
            } catch (err: any) {
                const errorMessage = err instanceof Error ? err.message : "Unknown error";
                console.error("[AUTH] Sign up failed", err);
                setError(errorMessage);

                track("auth_signup_failed", {
                    method: "email",
                    email,
                    error_message: errorMessage,
                    auth_type: getAuthType(),
                });

                throw err;
            } finally {
                setLoading(false);
            }
        },
        [
            loadSession,
            track,
            identify,
            getAuthType,
        ],
    );

    const signInWithGoogle = useCallback((redirectUri: string) => {
        captureGoogleOAuthStartBeforeRedirect(redirectUri);

        const params = new URLSearchParams({
            redirect_uri: redirectUri,
        });
        window.location.href = `/auth/backend/google?${params.toString()}`;
    }, []);

    const recordMarketingConsent = useCallback(
        async (marketingConsent: boolean, consentText: string) => {
            try {
                const response = await fetch("/auth/backend/marketing-consent", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: "include",
                    body: JSON.stringify({
                        marketing_consent: marketingConsent,
                        consent_text: consentText,
                    }),
                });

                if (!response.ok) {
                    throw new Error("Failed to record consent");
                }

                track("marketing_consent_recorded", {
                    user_id: user?.id,
                    marketing_consent: marketingConsent,
                });
            } catch (err) {
                // Non-fatal: never trap the user on the consent page. Log and move on.
                console.error("[AUTH] Failed to record marketing consent", err);
            } finally {
                // Reload session so needsMarketingConsent reflects the cleared marker.
                await loadSession();
            }
        },
        [
            loadSession,
            track,
            user,
        ],
    );

    const changePassword = useCallback(async (currentPassword: string, password: string, confirmPassword: string) => {
        let response: Response;
        let data;
        try {
            response = await fetch("/auth/backend/change-password", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    current_password: currentPassword,
                    password,
                    confirm_password: confirmPassword,
                }),
            });
            data = await response.json();
        } catch {
            return {
                password_change_unknown: true,
                error_description: "We could not confirm whether your password changed. Try signing in with your new password before retrying.",
            };
        }
        if (data.password_changed && data.access_token) {
            setAccessToken(data.access_token);
        }
        if (!response.ok && !data.password_changed && !data.password_change_unknown) {
            throw new Error(data.error_description || "Password change failed. Please try again.");
        }
        return data;
    }, []);

    const signOut = useCallback(
        async (destination?: "signin" | "password-recovery") => {
            const currentUser = user;
            setLoading(true);
            setError("");

            try {
                const response = await fetch("/auth/signout", {
                    method: "POST",
                    credentials: "include",
                });

                if (!response.ok) {
                    throw new Error("Failed to sign out");
                }

                // Navigation clears context; clearing user first lets auth guards replace
                // the recovery destination with their default login redirect.
                if (destination) {
                    const authUrl = import.meta.env.VITE_AUTH_URL || window.location.origin;
                    window.location.href = new URL(destination === "password-recovery" ? "/forgot-password" : "/", authUrl).href;
                    return;
                }
                setUser(null);
                setAccessToken(null);
                window.location.reload();
            } catch (err: any) {
                console.error("[AUTH] Failed to sign out", err);
                setError(err.message);

                track("auth_signout_failed", {
                    user_id: currentUser?.id,
                    error_message: err.message,
                });
            } finally {
                setLoading(false);
            }
        },
        [
            user,
            track,
        ],
    );

    const value: AuthContextType = {
        user,
        accessToken,
        loading,
        isSessionChecked,
        error,
        authenticated: !!user,
        needsMarketingConsent,
        signInWithEmail,
        signUpWithEmail,
        signInWithGoogle,
        recordMarketingConsent,
        changePassword,
        signOut,
        refresh: loadSession,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
